import {observer} from "mobx-react";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import {FONT_COLOR, FONT_VARIANT} from "../../../../../components/fonts/font-props";
import Card from "../../../../../components/layouts/card/card";
import Table from "../../../../../components/table/table";
import CustomerCaseStore from "../../stores/customer-case-store";
import ProductCaseStore from "../../stores/product-case-store";
import './rma-items-card.scss';
import RmaItemsTableRowList from "./rma-items-table-row-list/rma-items-table-row-list";
import TableRow from "../../../../../components/table/components/table-row/table-row";
import config from "../../../../../config";
import { useState } from "react";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { TABLE_SPACING_VARIANT } from "../../../../../components/table/table.types";
import { CARD_SPACING_VARIANT } from "../../../../../components/layouts/card/card.types";
import Currency from "../../../../../components/currency/currency";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import DashTranslation from "../../../../../dashboard-translation/dashboard-translaton";

interface IProps {
    productCaseList: ProductCaseStore[];
    customerCaseStore?: CustomerCaseStore;
    isLoading: boolean;
    isActionable: boolean;
}

const RmaPageItemsCard: React.FC<IProps> = observer(({isLoading, isActionable, productCaseList, customerCaseStore}) => {
    const productCaseListWithOffers = productCaseList.filter(pc => !pc.isHiddenResolution);
    const productCaseListWithNoOffers = productCaseList.filter(pc => pc.isHiddenResolution);
    const analyticsUrl = `${config.apiUrl}/dashboard/analytics/sessions?sessionId=${customerCaseStore?.id}`;
    const { getTranslation } = useDashboardTranslation();
    const scope = 'dashboard-returns-rma-card';

    const [isExpanded, setIsExpanded] = useState(true);

    return (
        <div className={'rma-page-items'}>
            <Card 
                isLoading={isLoading}
                toolbar={{
                    title: <div className="rma-page-items-title-container">
                        <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation("ORIGINAL_ITEMS", scope)}</FontBody1>
                        <FontBody2 color={FONT_COLOR.PRIMARY3_50}>{customerCaseStore?.numberOfItemsMessage}</FontBody2>
                    </div>,
                    content: customerCaseStore && (
                        <div className="rma-page-items-title-summary">
                            <div className="rma-page-items-title-summary-item">
                                <FontBody2 color={FONT_COLOR.PRIMARY2}>{getTranslation("RETURN_VALUE", scope)}</FontBody2>
                                <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                    <Currency currencyCode={customerCaseStore?.paymentCurrencyCode!} amount={customerCaseStore.totalReturnValueAmount}/>
                                </FontBody2>
                            </div>
                        </div>
                    )
                }}
                isExpandable={true}
                isExpanded={isExpanded}
                expandCallback={setIsExpanded}
                spacingVariant={CARD_SPACING_VARIANT.SMAll}
            >
                
                <div className="rma-page-items-table">
                    <Table columns={[
                        {content: '', width: {value: 16, type: 'px'}},
                        {content: getTranslation('ORIGINAL_ITEM', scope), width: {value: 2, type: 'fr'}},
                        {content: getTranslation('PAID_AMOUNT', scope), width: {value: 120, type: 'px'}},
                        {content: getTranslation('RETURN_REASON', scope), width: {value: 2, type: 'fr'}},
                        {content: getTranslation('POLICY_RULE', scope), width: {value: 3, type: 'fr'}},
                        {content: getTranslation('REQUEST_DETAILS', scope), width: {value: 2, type: 'fr'}},
                        {content: getTranslation('BALANCE', scope), width: {value: 1, type: 'fr'}},
                        {content: '', width: {value: 100, type: 'px'}},
                        {content: '', width: {value: 36, type: 'px'}},
                    ]}
                        spacingVariant={TABLE_SPACING_VARIANT.SMAll}
                    >
                        <RmaItemsTableRowList isActionable={isActionable} isOutOfPolicyItem={false} productCaseList={productCaseListWithOffers} />
                        {/** Show Out Of Policy Items Included in RMA according to settings */}
                        { productCaseListWithNoOffers.length > 0 &&
                            <>
                                <TableRow colspan={10} cells={[
                                    <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation('OUT_OF_POLICY', scope)}</FontBody2>
                                ]} />
                                <RmaItemsTableRowList isActionable={isActionable} isOutOfPolicyItem={true} productCaseList={productCaseListWithNoOffers} />
                             </>
                        }
                        {/** Show Out Of Policy Items Refference in RMA according to settings */}
                        {(customerCaseStore && customerCaseStore.numberOfReferencedOutOfPolicyItems > 0) &&
                            <TableRow colspan={10} cells={[
                                <div className="out-of-policy-refference">
                                    <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation('OUT_OF_POLICY', scope)}</FontBody2>
                                    <FontBody2 color={FONT_COLOR.NEUTRAL3}>
                                        {customerCaseStore.numberOfReferencedOutOfPolicyItems > 1 
                                            ? getTranslation('NUM_OF_ITEMS_OUT_OF_POLICY_PLURAL', scope, [customerCaseStore.numberOfReferencedOutOfPolicyItems.toString()])
                                            : getTranslation('NUM_OF_ITEMS_OUT_OF_POLICY_SINGLE', scope)
                                        }{' '}
                                        <span className="rma-items-out-of-policy-note">
                                            <DashTranslation translationKey={'CLICK_HERE_TO_VIEW_ITEM'} scope={scope} params={[analyticsUrl]} />
                                        </span>
                                    </FontBody2>
                                </div>
                            ]} />
                        }
                    </Table>
                </div>
            </Card>
        </div>
    );
});

export default RmaPageItemsCard;
